import * as React from 'react';
import { ReactNode, useContext, useEffect, useState } from 'react';
import { Layout } from '../Layout';
import SearchResultsList from './components/SearchResultsList';
import { SearchFacet, SearchRequest, SearchRequestSort } from '../../api/core';
import { PaginatorPageChangeEvent } from 'primereact/paginator';
import { defaultPageSize } from '../../Settings/config';
import { SearchFacets } from './components/SearchFacets';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import {
  FacetSetting,
  LabelledSearchRequestSort,
  SearchEntityRequest,
  SolrEntity,
  SolrResultSolrEntity,
} from '../../util/types';
import { Location, NavigateFunction, useLocation, useNavigate, useSearchParams } from 'react-router';
import { Button } from 'primereact/button';
import { ErrorComponent } from '../components/Error';
import { ToastContext, ToastContextType } from '../../util/providers/ToastProvider';
import { escapeSearchQuery } from './searchQueryEscape';

export interface ExternalSearchResultProps {
  externalParams?: ExternalSearchParams;
}

export interface SearchResultsProps extends ExternalSearchResultProps {
  searchFunction: (requestParameters: SearchEntityRequest) => Promise<SolrResultSolrEntity | null>;
  facetSettings: FacetSetting[];
  defaultSortOption: SearchRequestSort;
  sortOptions: LabelledSearchRequestSort[];
  title: ReactNode;
  renderResult: (result: SolrEntity, index: number) => ReactNode;
  path: string;
  headerImage: string;
}

export interface SearchQuery {
  q?: string;
  field?: string;
  direction?: string;
  fq?: QueryFacet[];
  foffset?: { [key: string]: number };
  page?: number;
  limit?: number;
}

export interface QueryFacet {
  facet: string;
  value: string;
  factor: boolean | null;
  missing?: boolean;
}

export type ExternalSearchParams = {
  facets?: { facet: string; value: string; factor: true; missing?: boolean }[];
  queryString?: string;
};

export function SearchResults(props: SearchResultsProps): ReactNode {
  const toast: ToastContextType = useContext(ToastContext);
  const intl: IntlShape = useIntl();

  const location: Location<any> = useLocation();
  const navigate: NavigateFunction = useNavigate();
  const [searchParams] = useSearchParams();

  const [searchQuery, setSearchQuery] = useState<SearchQuery>();
  const [fOffset, setFOffset] = React.useState<{ [key: string]: number }>({});
  const [searchResults, setSearchResults] = React.useState<SolrResultSolrEntity>(undefined);

  useEffect((): void => {
    const params: SearchQuery = {
      q: props.externalParams?.queryString || escapeSearchQuery(searchParams.get('q') || '') || '',
      field: searchParams.get('field') || props.defaultSortOption.field,
      direction: searchParams.get('direction') || props.defaultSortOption.direction,
      limit: parseInt(searchParams.get('limit'), 10) || defaultPageSize,
      page: parseInt(searchParams.get('page'), 10) - 1 || 0,
      fq: JSON.parse(searchParams.get('fq')) as QueryFacet[] || [],
    };
    setSearchQuery(params);
  }, [location]);

  useEffect((): void => {
    if(!searchQuery) return;
    void search();
  }, [searchQuery, fOffset]);

  const search = async (): Promise<void> => {
    const searchRequest: SearchRequest = {
      q: searchQuery.q,
      offset: Math.max(searchQuery.limit * searchQuery.page, 0),
      limit: searchQuery.limit,
      sort: {direction: searchQuery.direction, field: searchQuery.field},
      fq: (searchQuery.fq || []).map((facet: QueryFacet): string | null => {
        if (facet.missing) {
          return `-${facet.facet}:*`;
        }
        const facetSetting: FacetSetting = props.facetSettings.find(
          (setting: FacetSetting): boolean => setting.name === facet.facet && !!setting.parse);
        return facetSetting?.parse(facet) || null;
      })
        .filter((facetString: string): boolean => !!facetString),
      foffset: fOffset
    };

    try {
      const res: SolrResultSolrEntity = await props.searchFunction({ searchRequest});

      if(res.numberResults < searchQuery.limit * searchQuery.page) {
        return redirect({...searchQuery, page: Math.floor(res.numberResults / searchQuery.limit) + 1 });
      }

      const keys: string[] = Object.keys(fOffset)
        .filter((key: string): boolean => res.facets
          .some((f: SearchFacet): boolean => f.name === key && f.count > 0 && Object.keys(f.entries).length === 0));

      if(keys.length > 0){
        const fOffsetCopy = { ...fOffset };
        for (const key of keys) {
          delete fOffsetCopy[key];
        }
        return setFOffset(fOffsetCopy);
      }

      return setSearchResults(res);
    } catch (e) {
      toast.error({
        summary: intl.formatMessage({id: 'message.loading.failed.title'}),
        content: intl.formatMessage({id: 'message.loading.failed.message'}),
        severity: 'error',
        type: 'alert',
        response: e as unknown,
      });
      setSearchResults(null);
    }
  };

  const redirect = (searchRequest: SearchQuery): void => {
    const params = {
      q: searchRequest.q.length > 0 ? searchRequest.q : undefined,
      field: searchRequest?.field || undefined,
      direction: searchRequest?.direction || undefined,
      limit: searchRequest?.limit !== defaultPageSize ? searchRequest.limit : undefined,
      page: searchRequest?.page || 1,
      fq: searchRequest.fq.length > 0 ? JSON.stringify(searchRequest.fq) : undefined,
    };
    if (!props.externalParams) {
      void navigate({
        search: `?${
          Object.entries(params)
            .filter(x => x[1] !== undefined)
            .map((x: [string, any]): string => `${x[0]}=${encodeURIComponent(x[1] as string|number|boolean)}`)
            .join('&')
        }`, pathname: props.path,
      });
    } else {
      setSearchQuery({
        page: searchRequest.page - 1,
        limit: searchRequest.limit,
        fq: searchRequest.fq,
        direction: searchRequest.direction,
        field: searchRequest.field,
        q: searchRequest.q,
      });
    }
  };

  const onPageChange = (e: PaginatorPageChangeEvent): void => {
    redirect({...searchQuery, page: e.page + 1, limit: e.rows});
  };

  const onOrderChange = (e: SearchRequestSort): void => {
    redirect({...searchQuery, field: e.field, direction: e.direction});
  };

  const onFacetPageChange = (e: PaginatorPageChangeEvent, facet: string): void => {
    setFOffset((data: {[p: string]: number}): {[p: string]: number} => ({...data, [facet]: e.page * e.rows}));
  };

  const updateQueryFacets = (facet: QueryFacet): void => {
    const fq: QueryFacet[] = searchQuery.fq.filter(
      (qFacet: QueryFacet): boolean => qFacet.facet !== facet.facet ||
        qFacet.value !== facet.value ||
        !!qFacet.missing !== !!facet.missing);
    if(facet.factor !== null) {
      fq.push(facet);
    }
    redirect({...searchQuery, fq});
  };

  return (
    <Layout
      loading={searchResults === undefined}
      title={!props.externalParams ? props.title : undefined}
      headerImage={props.headerImage}
      sidebarTitle={
        <div className={'flex flex-row gap-3'}>
          <div className={'flex-grow-1'}>
            <FormattedMessage id="search.sidebar_heading" />
          </div>
          <div className={'flex-none'}>
            <div className={'flex flex-row gap-1'}>
              <Button
                icon={'pi pi-undo'}
                outlined={true}
                onClick={(): void => setSearchQuery({...searchQuery, fq: [], foffset: {}})}
                tooltip={intl.formatMessage({id: 'search.facets.reset.tooltip'})}
                tooltipOptions={{position: 'bottom'}}
              ></Button>
            </div>
          </div>
        </div>
      }
      sidebarButtonIcon={'pi pi-filter'}
      sidebarContent={
        searchResults !== undefined && searchResults !== null ? (
          <SearchFacets
            facets={searchResults?.facets || []}
            queryFacets={searchQuery.fq}
            onChange={updateQueryFacets}
            onFacetPageChange={onFacetPageChange}
            foffset={fOffset}
            facetsSettings={props.facetSettings}
            externalParams={props.externalParams}
          />
        ) : undefined
      }
    >
      {searchResults !== undefined && searchResults !== null ? (
        <SearchResultsList
          embeddedSearch={!!props.externalParams}
          results={searchResults?.results || []}
          total={searchResults?.numberResults || 0}
          offset={(searchQuery.page) * searchQuery.limit}
          rowsPerPage={searchQuery.limit}
          onPageChange={onPageChange}
          sort={{field: searchQuery.field, direction: searchQuery.direction}}
          onSortChange={onOrderChange}
          sortOptions={props.sortOptions}
          children={(searchResults?.results || []).map((result: SolrEntity, idx: number): ReactNode =>
            props.renderResult(result, idx + (searchQuery.page * searchQuery.limit))
          )}
        />
      ) : (
        <ErrorComponent />
      )}
    </Layout>
  );
}

export default SearchResults;
